import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Scheduler, { Resource, View } from "devextreme-react/scheduler";
import { useAppDispatch, useAppSelector } from "../../../Reducers/store";
import {
  fetchClientsWithLocationList,
  fetchUsers,
} from "../../../Actions/CommonFields";
import { toast } from "react-toastify";
import { Select } from "flowbite-react";
import JobApi from "../../../Api/JobApi";
import JobModal from "../JobModal";
import { UserLevelNames } from "../../../Models/UserLevel";
import ViewDailyRoute from "../../User/Driver/ViewDailyRoute";
import { SchedulerItemDto } from "../../../Models/SchedulerItemDto";
import DeliveryModal from "../Delivery/DeliveryModal";
import { Job } from "../../../Models/Job";
import { Delivery } from "../../../Models/Delivery";
import DeliveryApi from "../../../Api/DeliveryApi"; // ...imports unchanged...

const STATUS_RESOURCE = [
  { id: "Job", text: "Job", color: "#3B82F6" },
  { id: "InProcess", text: "In Process", color: "#DAA520" },
  { id: "Submitted", text: "Submitted", color: "#10B981" },
  { id: "Missed", text: "Missed", color: "#EF4444" },
  { id: "Approved", text: "Approved", color: "#F527B4" },
  { id: "Invoiced", text: "Invoiced", color: "#7C3AED" },
] as const;

const DriverFilterBar = memo(function DriverFilterBar({
  showDriverFilter,
  isDriver,
  driverFilter,
  setDriverFilter,
}: {
  showDriverFilter: boolean;
  isDriver: boolean;
  driverFilter: number;
  setDriverFilter: (value: number) => void;
}) {
  const { users } = useAppSelector((s) => s.commonFields);

  return (
    <>
      {showDriverFilter && (
        <div className="flex gap-1">
          <div className="content-center text-xl font-bold">Driver:</div>
          <Select
            id="driverFilter"
            name="driverFilter"
            onChange={(e) => setDriverFilter(Number(e.target.value))}
            value={driverFilter}
            sizing="md"
            required
          >
            <option key={0} value={0}>
              All
            </option>
            {users
              .filter((x) => x.userLevelId === Number(UserLevelNames.Driver))
              .map((obj) => (
                <option key={obj.id} value={obj.id}>
                  {obj.firstName} {obj.lastName}
                </option>
              ))}
          </Select>
        </div>
      )}
      {isDriver && (
        <div className="flex gap-1">
          <ViewDailyRoute />
        </div>
      )}
    </>
  );
});

export default function SchedulerDisplay() {
  const dispatch = useAppDispatch();
  const { userModel } = useAppSelector((s) => s.user);

  const schedulerRef = useRef<any>(null);
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log("[Scheduler] render", renderCount.current);

  // guards
  const didFirstContent = useRef(false);
  const isFetching = useRef(false);
  const hasScrolledToNow = useRef(false);
  const lastRange = useRef<{ s: number; e: number } | null>(null);
  const navTimer = useRef<number | null>(null);

  const [allData, setAllData] = useState<SchedulerItemDto[]>([]);
  const [driverFilter, setDriverFilter] = useState<number>(0);

  const [selectedJobForModal, setSelectedJobForModal] = useState<Job | null>(
    null
  );
  const [selectedDelivery, setselectedDelivery] = useState<Delivery | null>(
    null
  );

  const [showJobModal, setShowJobModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSchedulerVisible, setIsSchedulerVisible] = useState(false);

  // Check if device is mobile - declared early so it can be used in callbacks
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // For datetime-local inputs we must NOT use toISOString() (UTC), otherwise times drift by timezone offset.
  const toLocalDateTimeInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // references
  useEffect(() => {
    (async () => {
      await Promise.all([
        dispatch(fetchClientsWithLocationList()),
        dispatch(fetchUsers()),
      ]);
    })();
  }, [dispatch]);

  const filteredData = useMemo(() => {
    if (driverFilter === 0) return allData;
    return allData.filter((x) => (x.userId ?? 0) === driverFilter);
  }, [allData, driverFilter]);

  const isDriver =
    !!userModel && userModel.userLevelId === UserLevelNames.Driver;

  const fetchRange = useCallback(
    async (start: Date, end: Date) => {
      // Prevent concurrent fetches
      if (isFetching.current) {
        console.log("[Scheduler] fetchRange: skip (already fetching)");
        return;
      }

      try {
        isFetching.current = true;
        console.log(
          `[Scheduler] fetchRange: ${start.toISOString()} -> ${end.toISOString()}`
        );
        const res = await JobApi.getSchedulerData(
          start.toISOString(),
          end.toISOString()
        );
        const mapped = (res.data as SchedulerItemDto[]).map((x) => {
          // Parse datetime strings from backend, treating them as local time
          const parseLocalDateTime = (dateStr: string) => {
            if (!dateStr) return new Date();

            // If it's in YYYY-MM-DDTHH:mm format, parse as local time
            if (dateStr.includes("T") && !dateStr.includes("Z")) {
              const [datePart, timePart] = dateStr.split("T");
              const [year, month, day] = datePart.split("-").map(Number);
              const [hours, minutes] = timePart.split(":").map(Number);
              return new Date(year, month - 1, day, hours, minutes || 0);
            }

            return new Date(dateStr);
          };

          return {
            ...x,
            startDate: parseLocalDateTime(x.startDate) as any,
            endDate: parseLocalDateTime(x.endDate) as any,
          };
        });

        // Filter out deleted jobs and jobs that have deliveries on the same day
        const filteredMapped = mapped.filter((item) => {
          // Filter out deleted jobs
          if (item.deleted === true) {
            return false;
          }

          const isDelivery = item.deliveryId && item.deliveryId > 0;
          const status = item.status as string;
          const isHiddenForDriver =
            isDriver && (status === "Approved" || status === "Invoiced");

          // If this is a delivery (has deliveryId), show only if allowed
          if (isDelivery) {
            return !isHiddenForDriver;
          }

          // If this is a job (no deliveryId), check if there's a delivery for the same job on the same day
          const jobDate = new Date(item.startDate);
          const hasDeliveryOnSameDay = mapped.some((otherItem) => {
            if (!otherItem.deliveryId || otherItem.deliveryId <= 0)
              return false;
            const otherStatus = otherItem.status as string;
            if (
              isDriver &&
              (otherStatus === "Approved" || otherStatus === "Invoiced")
            )
              return false;
            if (otherItem.jobId !== item.jobId) return false;

            const deliveryDate = new Date(otherItem.startDate);
            return (
              jobDate.getFullYear() === deliveryDate.getFullYear() &&
              jobDate.getMonth() === deliveryDate.getMonth() &&
              jobDate.getDate() === deliveryDate.getDate()
            );
          });

          // Show the job only if there's no delivery on the same day
          return !hasDeliveryOnSameDay;
        });

        console.log(
          `[Scheduler] fetchRange: received ${mapped.length}, filtered ${filteredMapped.length}`
        );

        // Debug delivery times specifically
        const deliveryItems = filteredMapped.filter(
          (item) => item.deliveryId && item.deliveryId > 0
        );
        if (deliveryItems.length > 0) {
          console.log(
            `[Scheduler] fetchRange: delivery count ${deliveryItems.length}`
          );
        }
        setAllData(filteredMapped);
      } catch (err) {
        console.error("[Scheduler] fetchRange: error", err);
        toast.error("Failed to load schedule.");
      } finally {
        isFetching.current = false;
      }
    },
    [isDriver]
  );

  // keep startDayHour static (e.g., 5) and just scroll to now
  const scrollToNow = () => {
    const inst = schedulerRef.current?.instance;
    if (!inst) return;
    const view = inst.option("currentView");
    // only for day/week and only for "today"
    const curr = inst.option("currentDate") as Date;
    const today = new Date();
    const isToday =
      curr.getFullYear() === today.getFullYear() &&
      curr.getMonth() === today.getMonth() &&
      curr.getDate() === today.getDate();

    if (view !== "day" && view !== "week") return;
    if (!isToday) return;

    const now = new Date();
    let didScroll = false;

    if (typeof inst.scrollToTime === "function") {
      console.log("[Scheduler] scrollToTime", now.getHours(), now.getMinutes());
      inst.scrollToTime(now.getHours(), now.getMinutes(), now);
      didScroll = true;
    } else if (typeof inst.scrollTo === "function") {
      console.log("[Scheduler] scrollTo", now.toISOString());
      inst.scrollTo(now);
      didScroll = true;
    }

    const workSpace = inst.getWorkSpace?.();
    if (workSpace?.scrollToTime) {
      console.log(
        "[Scheduler] workspace.scrollToTime",
        now.getHours(),
        now.getMinutes()
      );
      workSpace.scrollToTime(now.getHours(), now.getMinutes(), now);
      didScroll = true;
    }

    // Manual fallback: scroll time-panel cell into view.
    try {
      const root = inst.element?.();
      const cellDuration = inst.option("cellDuration") ?? 30;
      const minutes = now.getHours() * 60 + now.getMinutes();
      const idx = Math.floor(minutes / cellDuration);
      const timeCells = root?.querySelectorAll?.(
        ".dx-scheduler-time-panel-cell"
      );
      const dateRows = root?.querySelectorAll?.(".dx-scheduler-date-table-row");
      const dateScrollContainer = root?.querySelector?.(
        ".dx-scheduler-date-table-scrollable .dx-scrollable-container"
      ) as HTMLElement | null;
      const timeScrollContainer = root?.querySelector?.(
        ".dx-scheduler-time-panel-scrollable .dx-scrollable-container"
      ) as HTMLElement | null;
      const targetCell = timeCells?.[idx] as HTMLElement | undefined;
      const targetRow = dateRows?.[idx] as HTMLElement | undefined;

      if (targetCell) {
        const targetTop = targetCell.offsetTop;
        const dateHeight = dateScrollContainer?.clientHeight ?? 0;
        const timeHeight = timeScrollContainer?.clientHeight ?? 0;
        const desiredTop =
          targetTop - Math.floor(Math.max(dateHeight, timeHeight) / 3);

        if (dateScrollContainer) {
          dateScrollContainer.scrollTop = desiredTop;
        }
        if (timeScrollContainer) {
          timeScrollContainer.scrollTop = desiredTop;
        }

        const workSpace = inst.getWorkSpace?.();
        const dateScrollable = workSpace?.getScrollable?.();
        const timePanelScrollable = workSpace?._timePanelScrollable;
        if (dateScrollable?.scrollTo) {
          dateScrollable.scrollTo({ top: desiredTop });
        }
        if (timePanelScrollable?.scrollTo) {
          timePanelScrollable.scrollTo({ top: desiredTop });
        }

        if (targetCell?.scrollIntoView) {
          targetCell.scrollIntoView({ block: "center" });
        }
        if (targetRow?.scrollIntoView) {
          targetRow.scrollIntoView({ block: "center" });
        }

        console.log("[Scheduler] manualScrollTop", {
          targetTop,
          desiredTop,
          dateHeight,
          timeHeight,
        });
        didScroll = true;
      }
    } catch (err) {
      console.log("[Scheduler] manualScrollTop error", err);
    }

    if (!didScroll) {
      console.log("[Scheduler] scrollToNow: no-op");
    }
  };

  // run after DX finishes laying out (prevents snap-back)
  const scrollToNowAfterPaint = (reason?: string) => {
    const inst = schedulerRef.current?.instance;
    if (inst) {
      inst.updateDimensions?.();
      inst.repaint?.();
    }
    if (reason) {
      console.log("[Scheduler] scrollToNowAfterPaint", reason);
    }
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        scrollToNow();
      })
    );
  };

  // Clamp helper
  const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

  // Remember the last applied startDayHour so we don't loop on optionChanged
  const lastStartHour = useRef<number | null>(null);

  // Compute the start hour: for today show "now" at the top; otherwise default (5)
  // Mobile-friendly: ensure early morning jobs are visible by always starting at 5 AM
  const computeStartHourForView = useCallback((inst: any) => {
    const view = inst.option("currentView");
    const currentDate: Date = inst.option("currentDate");
    const today = new Date();

    const isSameDay =
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getDate() === today.getDate();

    // For mobile/driver views, always start at 5 AM to ensure early jobs are visible
    const currentIsMobile = window.innerWidth < 1024;
    if (currentIsMobile || checkAccess()) {
      return 5; // Always show from 5 AM on mobile to ensure early jobs are visible
    }

    if ((view === "day" || view === "week") && isSameDay) {
      const nowH = new Date().getHours();
      // Desktop view can be more aggressive with scroll-to-now
      return clamp(nowH, 5, 23);
    }
    return 5; // default start hour for other days
  }, []);

  const applyStartHourIfNeeded = (inst: any) => {
    // Don't change startDayHour if we've already scrolled to current time on today
    // Changing it will reset the scroll position
    if (hasScrolledToNow.current) {
      const curr = inst.option("currentDate") as Date;
      const today = new Date();
      const isToday =
        curr.getFullYear() === today.getFullYear() &&
        curr.getMonth() === today.getMonth() &&
        curr.getDate() === today.getDate();
      if (isToday) {
        return; // Don't change startDayHour after scrolling on today
      }
    }
    const desired = computeStartHourForView(inst);
    if (lastStartHour.current !== desired) {
      lastStartHour.current = desired;
      inst.option("startDayHour", desired);
    }
  };
  // First content: kick off fetch AND scroll now (UI can scroll even before data arrives)
  const onContentReady = useCallback(() => {
    const inst = schedulerRef.current?.instance;
    if (!inst) return;

    // Only run once - guard against multiple calls
    if (didFirstContent.current) {
      // Already initialized, don't scroll again
      console.log("[Scheduler] onContentReady: skip (already initialized)");
      return;
    }

    didFirstContent.current = true;
    console.log("[Scheduler] onContentReady: init");

    // Check if we're viewing today
    const curr = inst.option("currentDate") as Date;
    const today = new Date();
    const isToday =
      curr.getFullYear() === today.getFullYear() &&
      curr.getMonth() === today.getMonth() &&
      curr.getDate() === today.getDate();

    // Apply start hour BEFORE fetching data.
    // For mobile/driver or non-today views: always use 5 to ensure early jobs visible.
    // For desktop on today: keep startDayHour at 5 and scroll to now after data bind.
    const currentIsMobile = window.innerWidth < 1024;
    const isDriverView = checkAccess();
    const isDesktopToday = isToday && !currentIsMobile && !isDriverView;
    if (isDesktopToday) {
      console.log("[Scheduler] onContentReady: desktop today startDayHour 5");
      inst.option("startDayHour", 5);
      lastStartHour.current = 5;
      hasScrolledToNow.current = false;
    } else {
      console.log("[Scheduler] onContentReady: startDayHour 5");
      inst.option("startDayHour", 5);
      lastStartHour.current = 5;
    }

    // Don't scroll yet - wait for data to load first
    const start = inst.getStartViewDate();
    const end = inst.getEndViewDate();
    lastRange.current = { s: +start, e: +end };
    void fetchRange(start, end).then(() => {
      // DON'T change startDayHour after data loads - it will reset scroll position
      // Only scroll once after initial data load if we're viewing today
      // Wait for scheduler to fully render data before scrolling
      if (isDesktopToday) {
        console.log("[Scheduler] onContentReady: schedule scrollToNow");
        // Show scheduler before scrolling so the scroll takes effect.
        setIsInitialLoading(false);
        setIsSchedulerVisible(true);
        console.log("[Scheduler] visible: true (before scroll)");
        // Use a delayed scroll after data is bound and rendered.
        setTimeout(() => {
          hasScrolledToNow.current = true;
          console.log("[Scheduler] onContentReady: scrollToNow");
          scrollToNowAfterPaint("after data bind");
          // One extra attempt to stabilize the scroll after layout.
          setTimeout(() => scrollToNowAfterPaint("after data bind retry"), 200);
        }, 400);
      } else if (!hasScrolledToNow.current && isToday) {
        console.log("[Scheduler] onContentReady: schedule scrollToNow");
        setTimeout(() => {
          if (!hasScrolledToNow.current) {
            hasScrolledToNow.current = true;
            console.log("[Scheduler] onContentReady: scrollToNow");
            scrollToNowAfterPaint("after data bind (non-desktop)");
          }
          setIsInitialLoading(false);
          setIsSchedulerVisible(true);
          console.log("[Scheduler] visible: true (after scroll)");
        }, 400);
      } else {
        setIsInitialLoading(false);
        setIsSchedulerVisible(true);
        console.log("[Scheduler] visible: true (no scroll)");
      }
    });
  }, [fetchRange]);

  // Navigation: scroll immediately, then fetch
  const onOptionChanged = useCallback(
    (e: any) => {
      if (
        e.name !== "currentDate" &&
        e.name !== "currentView" &&
        e.name !== "dataSource"
      )
        return;

      // Don't handle navigation changes during initial load
      if (!didFirstContent.current) return;

      console.log("[Scheduler] onOptionChanged:", e.name);

      // Apply dynamic start hour when view or date changes
      const inst = schedulerRef.current?.instance;
      if (inst) {
        const curr = inst.option("currentDate") as Date;
        const today = new Date();
        const isToday =
          curr.getFullYear() === today.getFullYear() &&
          curr.getMonth() === today.getMonth() &&
          curr.getDate() === today.getDate();
        const shouldPreserveScroll = hasScrolledToNow.current && isToday;

        // If user navigates away from today, allow auto-scroll when they return
        if (!isToday) {
          hasScrolledToNow.current = false;
        }

        // Force it to 5 for mobile or non-today views to ensure early jobs are visible
        const currentIsMobile = window.innerWidth < 1024;
        const isDesktopToday = isToday && !currentIsMobile && !checkAccess();
        const shouldForceStartHour =
          currentIsMobile || checkAccess() || !isToday;
        if (!shouldPreserveScroll && shouldForceStartHour && !isDesktopToday) {
          console.log("[Scheduler] onOptionChanged: set startDayHour 5");
          inst.option("startDayHour", 5);
          lastStartHour.current = 5;
        }

        // If data source changes (after fetch), re-assert "now" on desktop today.
        if (e.name === "dataSource") {
          if (isToday && !currentIsMobile && !checkAccess()) {
            console.log(
              "[Scheduler] onOptionChanged: dataSource -> scrollToNow"
            );
            scrollToNowAfterPaint();
          }
          return;
        }

        // Only scroll if user navigated to today
        if (isToday) {
          console.log("[Scheduler] onOptionChanged: scrollToNow");
          scrollToNowAfterPaint();
        }
      }

      if (navTimer.current) window.clearTimeout(navTimer.current);
      navTimer.current = window.setTimeout(() => {
        const inst = schedulerRef.current?.instance;
        if (!inst) return;
        const start = inst.getStartViewDate();
        const end = inst.getEndViewDate();
        const s = +start,
          eNum = +end;
        if (
          lastRange.current &&
          lastRange.current.s === s &&
          lastRange.current.e === eNum
        )
          return;
        lastRange.current = { s, e: eNum };
        void fetchRange(start, end);
      }, 150);
    },
    [fetchRange]
  );

  useEffect(() => {
    // Reset guards when component unmounts
    return () => {
      if (navTimer.current) window.clearTimeout(navTimer.current);
      didFirstContent.current = false;
      isFetching.current = false;
      hasScrolledToNow.current = false;
    };
  }, []);

  const onAppointmentClick = (e: any) => {
    e.cancel = true;
    const appt: SchedulerItemDto = e.appointmentData;

    if (appt.deliveryId && appt.deliveryId > 0) {
      DeliveryApi.getDelivery(String(appt.deliveryId))
        .then((res) => {
          setselectedDelivery(res.data as Delivery);
          setShowDeliveryModal(true);
        })
        .catch(() => {
          setselectedDelivery({
            id: appt.deliveryId!,
            clientLocationId: null,
            jobId: appt.jobId ?? null,
            deliveredBy: null,
            dateDelivered: null,
            signature: null,
            startTime: appt.startDate as any,
            endTime: appt.endDate as any,
            inMileage: null,
            outMileage: null,
            acceptedBy: null,
            statusId: null,
            quickBooksId: null,
            notes: null,
            clientId: null,
            clientContactId: null,
            dateInvoiced: null,
            invoiceNumber: null,
            client: null,
            clientLocation: null,
            deliveryItems: [],
            status: null,
            deliveryAssets: [],
          } as Delivery);
          setShowDeliveryModal(true);
        });
    } else {
      setClickedDate(new Date(appt.startDate as any));
      JobApi.getJob(String(appt.jobId))
        .then((res) => {
          setSelectedJobForModal(res.data as Job);
          setShowJobModal(true);
        })
        .catch(() => {
          setSelectedJobForModal({
            id: appt.jobId,
            clientLocationId: null,
            dateCreated: null,
            jobNumber: null,
            startDate: toLocalDateTimeInput(new Date(appt.startDate as any)),
            endDate: "",
            maxAmount: null,
            terms: null,
            notes: null,
            clientId: null,
            userId: appt.userId ?? null,
            frequencyId: null,
            day: null,
            isRecurring: false,
            deleted: false,
            client: null,
            jobItems: [],
            clientLocation: null,
            frequency: null,
            user: null,
          } as Job);
          setShowJobModal(true);
        });
    }
  };

  const onCellClick = (e: any) => {
    e.cancel = true;
    if (!userModel || isDriver) {
      // Do not allow drivers to open the add job modal
      return;
    }
    const start = e.cellData.startDate as Date;
    setClickedDate(start);
    setSelectedJobForModal({
      id: 0,
      clientLocationId: null,
      dateCreated: null,
      jobNumber: null,
      startDate: toLocalDateTimeInput(start),
      endDate: "",
      maxAmount: null,
      terms: null,
      notes: null,
      clientId: null,
      userId: null,
      frequencyId: null,
      day: null,
      isRecurring: false,
      deleted: false,
      client: null,
      jobItems: [],
      clientLocation: null,
      frequency: null,
      user: null,
    } as Job);
    setShowJobModal(true);
  };

  const checkAccess = () => isDriver;

  const appointmentRender = (data: any) => {
    const a: SchedulerItemDto = data.appointmentData;
    return (
      <div className="text-sm">
        <strong>{a.client}</strong>
        <br />
        {a.siteName}
      </div>
    );
  };

  const AppointmentTooltip = (model: any) => {
    const a: SchedulerItemDto = model.data.appointmentData;
    return (
      <div className="">
        <div className="text-xl font-bold">{a.client}</div>
        <div>
          Address: {a.address} {a.city}
          {a.state ? `, ${a.state}` : ""}
        </div>
        <div>Type: {a.status}</div>
      </div>
    );
  };

  const handleDeliveryStarted = (deliveryId: number, jobId: number) => {
    console.log(
      `handleDeliveryStarted called with deliveryId: ${deliveryId}, jobId: ${jobId}`
    );

    // Add a small delay to ensure the delivery is created in the database
    setTimeout(() => {
      DeliveryApi.getDelivery(String(deliveryId))
        .then((res) => {
          console.log("Successfully fetched delivery:", res.data);
          setselectedDelivery(res.data as Delivery);
          setShowDeliveryModal(true);
          // optional: refresh the visible range
          const inst = schedulerRef.current?.instance;
          if (inst) {
            const start = inst.getStartViewDate();
            const end = inst.getEndViewDate();
            lastRange.current = { s: +start, e: +end };
            void fetchRange(start, end);
          }
        })
        .catch((error) => {
          console.error("Error fetching delivery:", error);
          toast.error(
            `Failed to fetch delivery details: ${
              error.message || "Unknown error"
            }`
          );

          // Fallback if fetch fails: open modal with minimal data
          setselectedDelivery({
            id: deliveryId,
            clientLocationId: null,
            jobId,
            deliveredBy: null,
            dateDelivered: null,
            signature: null,
            startTime: null,
            endTime: null,
            inMileage: null,
            outMileage: null,
            acceptedBy: null,
            statusId: null,
            quickBooksId: null,
            notes: null,
            clientId: null,
            clientContactId: null,
            dateInvoiced: null,
            invoiceNumber: null,
            client: null,
            clientLocation: null,
            deliveryItems: [],
            status: null,
            deliveryAssets: [],
          } as Delivery);
          setShowDeliveryModal(true);
        });
    }, 500); // 500ms delay to ensure delivery is created
  };

  const views: any[] = ["day", "week"];
  const driverViews: any[] = ["day"];

  // Handle mobile detection and view switching - only when window size changes
  useEffect(() => {
    const checkMobile = () => {
      const nowMobile = window.innerWidth < 1024; // lg breakpoint
      const wasMobile = isMobile;

      // Only update state if it actually changed
      if (wasMobile !== nowMobile) {
        setIsMobile(nowMobile);

        // If mobile state changed, switch view
        if (schedulerRef.current?.instance) {
          const scheduler = schedulerRef.current.instance;
          // Check if we're on today - if so, don't change startDayHour if we've scrolled
          const curr = scheduler.option("currentDate") as Date;
          const today = new Date();
          const isToday =
            curr.getFullYear() === today.getFullYear() &&
            curr.getMonth() === today.getMonth() &&
            curr.getDate() === today.getDate();
          const shouldPreserveScroll = hasScrolledToNow.current && isToday;

          if (nowMobile) {
            // Switching to mobile - use day view and apply proper start hour
            scheduler.option("currentView", "day");
            // Only set startDayHour if we haven't scrolled to current time yet
            // Changing it after scrolling will reset scroll position
            if (!shouldPreserveScroll) {
              scheduler.option("startDayHour", 5);
              lastStartHour.current = 5;
            }
          } else if (!checkAccess()) {
            // Switching to desktop and not a driver - use week view
            scheduler.option("currentView", "week");
            // Only apply start hour if we haven't scrolled yet
            if (!shouldPreserveScroll) {
              applyStartHourIfNeeded(scheduler);
            }
          }
        }
      }
    };

    // Initial check - but don't run if we've already initialized
    if (!didFirstContent.current) {
      checkMobile();
    }

    // Use a debounced resize handler to prevent excessive calls
    let resizeTimer: number | null = null;
    const handleResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(checkMobile, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimer) window.clearTimeout(resizeTimer);
    };
  }, [isMobile]);

  return (
    <div className="flex h-full flex-col min-h-0">
      <div className="flex gap-1 w-full justify-center p-2">
        <DriverFilterBar
          showDriverFilter={!!userModel && !isDriver}
          isDriver={isDriver}
          driverFilter={driverFilter}
          setDriverFilter={setDriverFilter}
        />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4 pb-2 text-sm text-gray-700">
        {STATUS_RESOURCE.map((status) => (
          <div key={status.id} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: status.color }}
              aria-hidden
            />
            <span>{status.text}</span>
          </div>
        ))}
      </div>

      <div className="relative flex-1 min-h-0 overflow-auto">
        {isInitialLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-sm text-gray-700">
            Loading schedule...
          </div>
        )}
        <Scheduler
          id="scheduler"
          ref={schedulerRef}
          className="h-full"
          style={{ visibility: isSchedulerVisible ? "visible" : "hidden" }}
          dataSource={filteredData}
          showAllDayPanel={false}
          adaptivityEnabled={true}
          views={isDriver || isMobile ? driverViews : views}
          defaultCurrentView={isDriver || isMobile ? "day" : "week"}
          appointmentTooltipComponent={AppointmentTooltip}
          onCellClick={onCellClick}
          onAppointmentClick={onAppointmentClick}
          appointmentRender={appointmentRender}
          recurrenceEditMode="series"
          timeZone="America/New_York"
          textExpr="title"
          startDateExpr="startDate"
          endDateExpr="endDate"
          showCurrentTimeIndicator
          indicatorUpdateInterval={60_000}
          onContentReady={onContentReady}
          onOptionChanged={onOptionChanged}
        >
          <View type="week" maxAppointmentsPerCell={"unlimited"} />
          <View type="day" maxAppointmentsPerCell={"unlimited"} />
          <View type="month" maxAppointmentsPerCell={"unlimited"} />
          <Resource
            dataSource={STATUS_RESOURCE}
            fieldExpr="status"
            label="Status"
            useColorAsDefault={true}
          />
        </Scheduler>
      </div>

      <JobModal
        show={showJobModal}
        setShow={setShowJobModal}
        getSchedulerData={() => {
          const inst = schedulerRef.current?.instance;
          if (!inst) return;
          const start = inst.getStartViewDate();
          const end = inst.getEndViewDate();
          lastRange.current = { s: +start, e: +end };
          void fetchRange(start, end);
        }}
        clickedDate={clickedDate}
        selectedJob={selectedJobForModal}
        setSelectedJob={setSelectedJobForModal}
        onDeliveryStarted={handleDeliveryStarted}
      />

      <DeliveryModal
        show={showDeliveryModal}
        setShow={setShowDeliveryModal}
        getSchedulerData={() => {
          const inst = schedulerRef.current?.instance;
          if (!inst) return;
          const start = inst.getStartViewDate();
          const end = inst.getEndViewDate();
          lastRange.current = { s: +start, e: +end };
          void fetchRange(start, end);
        }}
        selectedDelivery={selectedDelivery}
        setselectedDelivery={setselectedDelivery}
      />
    </div>
  );
}
